<script>
  import { changeColInUri } from '../../routes/design/[productSlug]/_design-functions';

  import {
    uploadPanelState,
    processedImages,
    singleColourLogoColour,
    selectedImage,
    selectedCategory,
    selectedVersion,
  } from '../../stores/UploadPanelState.js';
  import { logoState } from '../../stores/Logos.js';
  import { productDataStore } from '../../stores/ProductData.js';
  import { isMobile, designTabActive } from '../../stores/Designer.js';
  import { toastStore } from '../../stores/Toast.js';

  import LoadingSpinner from '../LoadingSpinner.svelte';
  import ColoredButton from './ColoredButton.svelte';
  import DesignCard from './DesignCard.svelte';
  import UploadImageSelectionCard from './UploadImageSelectionCard.svelte';
  import ColourPicker from './ColourPicker.svelte';
  import PrintTypeSelection from './PrintTypeSelection.svelte';

  export let addToKonva;
  export let segment;
  export let updateInKonva;
  export let editingElement;
  export let icons;

  let dragging = false;
  let input;
  let file;
  let processing = false;
  const types = ['jpg', 'png', 'svg', 'ai', 'pdf', 'eps', 'ps'];

  let printOptions = $productDataStore[$productDataStore.showing].printOptions;

  let printOption = printOptions[0]?.PrintOption?.name || 'digital transfer';

  const allowedMimeTypes = [
    'image/jpeg',
    'image/jpg',
    'image/png',
    'image/svg+xml',
    'application/postscript',
    'application/pdf',
    'application/eps',
  ];

  // Background colour of selected product item - to show preview against
  let selectedProductColourMain = $productDataStore[$productDataStore.showing].selectedColour.ProductColoursMasterMain;

  let selectedProductColourTrim1 =
    $productDataStore[$productDataStore.showing].selectedColour.ProductColoursMasterTrim1;

  let selectedProductColourTrim2 =
    $productDataStore[$productDataStore.showing].selectedColour.ProductColoursMasterTrim2;

  let editingElementLoaded = false;
  $: if ($editingElement && !editingElementLoaded) {
    // set all variables to values from editing element
    selectPreviousLogo($editingElement.uploadData.id);
    printOption = $editingElement.printOption || 'digital transfer';
    editingElementLoaded = true;
  }

  // When transformer is detached editingElement becomes null
  // Need to reset variables to defaults in this scenario
  $: if (!$editingElement && editingElementLoaded) {
    $uploadPanelState = 'upload';
    $processedImages = null;
    $selectedImage = null;
    $selectedCategory = 'original';
    $selectedVersion = 'original';
    editingElementLoaded = false;
    printOption = printOptions[0]?.PrintOption?.name || 'digital transfer';
  }

  // Add a handler to update state when printOption changes for editing element
  function handlePrintOptionSelect() {
    if ($editingElement) {
      addLogo();
    }
  }

  // https://svelte.dev/repl/0ace7a508bd843b798ae599940a91783?version=3.16.7
  // Function to detect click outside of colour picker selectors - so we close them
  function clickOutside(node) {
    const handleClick = (event) => {
      if (node && !node.contains(event.target) && !event.defaultPrevented) {
        node.dispatchEvent(new CustomEvent('click_outside', node));
      }
    };
    document.addEventListener('click', handleClick, true);
    return {
      destroy() {
        document.removeEventListener('click', handleClick, true);
      },
    };
  }

  let colourPickerShow = false;

  // This function is called by each colour picker when a click is outside
  function handleClickOutside() {
    colourPickerShow = false;
  }

  function verifyFile(file) {
    let valid = false;
    let size = false;
    let type = false;
    if (file.size < 5242880) {
      size = true;
    }
    if (allowedMimeTypes.includes(file.type)) {
      type = true;
    }

    // Only if all conditions pass do we set valid to true
    if (size && type) {
      valid = true;
    }
    return valid;
  }

  function openFilePicker() {
    input.click();
  }

  async function generateFile(colour) {
    const body = new FormData();
    body.append('file', file);
    body.append('colour', colour);

    try {
      const res = await fetch('https://services.uniformhero.com.au/uploads', {
        method: 'POST',
        body: body,
      });
      if (res.error) {
        throw new Error(res.error);
      }
      return await res.json();
    } catch (error) {
      throw new Error(error);
    }
  }

  async function selectFile() {
    processing = true;
    file = this.files[0];
    // determine the colour for single colour images
    const colourTo = selectedProductColourTrim1 ? selectedProductColourTrim1.rgb : selectedProductColourMain.rgb;
    if (verifyFile(file)) {
      try {
        $processedImages = await generateFile(colourTo);
      } catch (error) {
        console.log(error);
        toastStore.trigger(
          'There was an error uploading your file. Please Refresh the Page and Try Again or Contact Support for Assistance.',
        );
      }

      // Set selectedImage
      $selectedImage = $processedImages.images.original.original;

      processing = false;
      $uploadPanelState = 'processed';
    } else {
      //TODO - some sort of error flashing
      alert('error validating file');
      processing = false;
      file = null;
      // Do not update state
    }
  }

  async function dropped(e) {
    processing = true;
    e.preventDefault();
    file = e.dataTransfer.files[0];
    dragging = false;
    // determine the colour for single colour images
    const colourTo = selectedProductColourTrim1 ? selectedProductColourTrim1.rgb : selectedProductColourMain.rgb;
    if (verifyFile(file)) {
      try {
        $processedImages = await generateFile(colourTo);
      } catch (error) {
        toastStore.trigger(
          'There was an error uploading your file. Please Refresh the Page and Try Again or Contact Support for Assistance.',
        );
      }
      processing = false;
      $uploadPanelState = 'processed';
    } else {
      toastStore.trigger(
        'The file you uploaded was in a format that we are not able to process. Please Try Again with an Alternative File, or Contact Support for Assistance.',
      );
      processing = false;
      file = null;
    }
  }

  function setSelectedImage(selectionObject) {
    $selectedImage = $processedImages.images[selectionObject.category][selectionObject.selection];

    $selectedCategory = selectionObject.category;
    $selectedVersion = selectionObject.selection;

    if ($editingElement) {
      // update image in konva
      addLogo();
    }
  }

  function addLogo() {
    let details = {
      printType: 'upload',
      productType: $productDataStore.showing,
      segment: segment,
      image: $selectedImage,
      uploadData: $processedImages,
      selectedCategory: $selectedCategory,
      selectedVersion: $selectedVersion,
      printOption: printOption,
    };

    if ($editingElement) {
      updateInKonva($editingElement, $selectedImage, details);
    } else {
      addToKonva($selectedImage, details);
      // Save selected logo into state - include ID of original. So can re-edit
      logoState.addLogo($processedImages, $selectedCategory, $selectedVersion);
      // TODO - if logged in persist logo data to user account
      // Revert to upload state
      $uploadPanelState = 'upload';
    }
  }

  function updateSingleColourImageUrls(colourTo, dimensions) {
    // Update each single colour image
    for (const [key, value] of Object.entries($processedImages.images.singleColour)) {
      changeColInUri(value, colourTo, dimensions.width, dimensions.height, (data) =>
        updateSingleColourImageSrc(data, key),
      );
    }

    // Update colour to store in state.
    $singleColourLogoColour = colourTo;
  }

  // Callback for the changeColInUri function - sets the value
  function updateSingleColourImageSrc(dataUri, version) {
    $processedImages.images.singleColour[version] = dataUri;

    // update selectedImage here as well
    if ($selectedCategory === 'singleColour' && $selectedVersion === version) {
      $selectedImage = dataUri;
      // if editing update logo on preview panel as well
      if ($editingElement) {
        addLogo();
      }
    }
  }

  function updateLogoColour(item) {
    updateSingleColourImageUrls(item.rgb, $processedImages.dimensions);
  }

  // TODO - this is broken when editing from cart
  function selectPreviousLogo(logoId) {
    let logo = $logoState.find((x) => x.data.id === logoId);
    $processedImages = logo.data;
    $selectedCategory = logo.selectedCategory;
    $selectedVersion = logo.selectedVersion;
    $selectedImage = logo.data.images[logo.selectedCategory][logo.selectedVersion];
    $uploadPanelState = 'processed';
  }
</script>

<section>
  {#if $uploadPanelState === 'upload'}
    <div class="notice">
      We will fix any logo, upload and order. Our team will ensure a high quality print and contact you if necessary.<br
      />
      <br />
      If you have any issues uploading your logo please <a href="/contact">contact us</a>.
    </div>
    {#if $logoState && $logoState.length > 0}
      <div class="previous-logo-heading">Your Previous Logos</div>
      <div class="previous-logos">
        {#each $logoState as logo}
          <!-- svelte-ignore a11y-click-events-have-key-events -->
          <div class="previous-logo" on:click={() => selectPreviousLogo(logo.data.id)}>
            <img src={logo.data.images[logo.selectedCategory][logo.selectedVersion]} alt="logo" />
          </div>
        {/each}
      </div>
    {/if}
    <div
      class="drag-area"
      on:drop={dropped}
      on:dragover={(event) => {
        event.preventDefault();
        dragging = true;
      }}
      on:dragleave={() => {
        dragging = false;
      }}
    >
      {#if !processing}
        <img class="upload-icon" src="img/design/font/Upload-Box.svg" alt="Upload" />
        <div class="instruction">
          {#if dragging}
            Release
          {:else}
            <!-- svelte-ignore a11y-click-events-have-key-events -->
            Drag & Drop or <span class="orange" on:click={openFilePicker}>Browse</span>
            on Your Computer.
          {/if}
        </div>
        <input
          type="file"
          on:change={selectFile}
          hidden
          bind:this={input}
          accept=".jpg, .png, .svg, .ai, .pdf, .eps, .ps"
        />
      {:else}
        <LoadingSpinner />
        Processing Image<br />
        <span id="processing-message">This may take a few moments</span>
      {/if}
    </div>

    <div class="supported">
      <span class="info">Accepted File Types (Max size: 2 MB)</span>

      <div class="types">
        {#each types as item}
          <DesignCard clickable={false}>
            .{item}
          </DesignCard>
        {/each}
      </div>
    </div>
    <div class="copyright">
      By uploading a logo here, you affirm your copyright ownership or authorised use, accepting all legal
      responsibility for any infringement claims; read our full <a href="/terms-conditions">terms and conditions</a> for
      details.
    </div>
  {/if}

  {#if $uploadPanelState === 'processed'}
    {#if !$editingElement}
      <!-- svelte-ignore a11y-click-events-have-key-events -->
      <div
        class="back"
        on:click={() => {
          $uploadPanelState = 'upload';
        }}
      >
        <div class="back-icon">
          {@html icons.arrowLeft}
        </div>
        Back
      </div>
    {/if}
    <div class="preview">
      <img src={$selectedImage} alt="Text Preview" style="--bgColour: rgb{selectedProductColourMain.rgb}" /><br />
      Preview
    </div>
    <div class="selection-container">
      <div class="heading">
        Select Your Preferred Version of the Logo<span class="more-info">i</span>
      </div>
      <div class="processed-label">Original Logo Variants</div>
      <div class="processed-images">
        <UploadImageSelectionCard
          img={{
            src: $processedImages.images.original.original,
            alt: 'original image',
          }}
          backgroundColour={selectedProductColourMain.rgb}
          selected={$selectedImage === $processedImages.images.original.original}
          clickFn={() =>
            setSelectedImage({
              category: 'original',
              selection: 'original',
            })}
        />
        <UploadImageSelectionCard
          img={{
            src: $processedImages.images.original.trimmed,
            alt: 'trimmed image',
          }}
          backgroundColour={selectedProductColourMain.rgb}
          selected={$selectedImage === $processedImages.images.original.trimmed}
          clickFn={() =>
            setSelectedImage({
              category: 'original',
              selection: 'trimmed',
            })}
        />
      </div>
      <div class="processed-label">Background Removed Variants</div>
      <div class="processed-images">
        {#each Object.entries($processedImages.images.transparent) as [version, imageUrl]}
          <UploadImageSelectionCard
            img={{
              src: imageUrl,
              alt: `transparent version ${version}`,
            }}
            backgroundColour={selectedProductColourMain.rgb}
            selected={$selectedImage === imageUrl}
            clickFn={() =>
              setSelectedImage({
                category: 'transparent',
                selection: version,
              })}
          />
        {/each}
      </div>
      {#if $processedImages.images.backgroundRemoved}
        <div class="processed-label">Background Removed Variants</div>
        <div class="processed-images">
          {#each Object.entries($processedImages.images.backgroundRemoved) as [version, imageUrl]}
            <UploadImageSelectionCard
              img={{
                src: imageUrl,
                alt: `Background removed version ${version}`,
              }}
              backgroundColour={selectedProductColourMain.rgb}
              selected={$selectedImage === imageUrl}
              clickFn={() =>
                setSelectedImage({
                  category: 'backgroundRemoved',
                  selection: version,
                })}
            />
          {/each}
        </div>
      {/if}
      <div class="processed-label">Single Colour Variants</div>
      <div class="processed-images">
        {#each Object.entries($processedImages.images.singleColour) as [version, imageUrl]}
          <UploadImageSelectionCard
            img={{
              src: imageUrl,
              alt: `Single Colour version ${version}`,
            }}
            backgroundColour={selectedProductColourMain.rgb}
            selected={$selectedImage === imageUrl}
            clickFn={() =>
              setSelectedImage({
                category: 'singleColour',
                selection: version,
              })}
          />
        {/each}
      </div>
      <div class="colourpicker" use:clickOutside on:click_outside={handleClickOutside}>
        <ColourPicker
          selectedRgb={selectedProductColourTrim1 ? selectedProductColourTrim1.rgb : selectedProductColourMain.rgb}
          selectedProductColourName={selectedProductColourTrim1
            ? selectedProductColourTrim1.name
            : selectedProductColourMain.name}
          clickFn={updateLogoColour}
          mainColour={selectedProductColourMain}
          trim1Colour={selectedProductColourTrim1}
          trim2Colour={selectedProductColourTrim2}
          bind:show={colourPickerShow}
        />
      </div>
    </div>
  {/if}

  {#if printOptions.length > 1}
    <div class="print-type-selection">
      <PrintTypeSelection {printOptions} bind:printOption changeFn={handlePrintOptionSelect} />
    </div>
  {/if}

  <div class="grid">
    {#if !$editingElement && $uploadPanelState !== 'upload'}
      <ColoredButton label="+ Add Image" type="green" clickFn={addLogo} />
    {/if}
    {#if $isMobile && $designTabActive}
      {#if $editingElement}
        <ColoredButton label="Update" type="green" clickFn={() => ($designTabActive = false)} />
      {/if}
      <ColoredButton label="Close" type="red" clickFn={() => ($designTabActive = false)} />
    {/if}
  </div>
</section>

<style>
  section {
    margin: 0;
    padding: 2.2rem 0;
    display: flex;
    flex-direction: column;
  }

  .notice {
    color: #666;
    font-size: 1.2rem;
    font-weight: 600;
    font-family: 'Inter';
    margin-bottom: 2rem;
  }

  .drag-area {
    width: 100%;
    border-radius: 2rem;
    height: 20rem;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    border: 2px dashed #d6d3d3;
    padding: 1.5rem;
    font-size: 1.6rem;
    font-weight: 500;
    font-family: 'Inter', sans-serif;
  }

  .instruction {
    margin-top: 1rem;
    text-align: center;
    max-width: 20rem;
  }

  .upload-icon {
    width: 5rem;
    height: 5rem;
  }

  .instruction .orange {
    color: #fe5900;
    text-decoration: underline;
  }

  .supported {
    border-top: 1px solid rgba(0, 0, 0, 0.15);
    padding-top: 2.2rem;
    margin-top: 2.2rem;
  }

  .supported .info {
    color: #666;
    display: block;
    margin-bottom: 2rem;
    font-weight: 500;
  }

  .copyright {
    margin-top: 1.5rem;
    font-size: 1.2rem;
    color: #666;
  }

  .grid {
    margin-top: 2rem;
    display: grid;
    column-gap: 2rem;
    grid-template-columns: repeat(2, 1fr);
  }

  .types {
    display: grid;
    grid-template-columns: repeat(5, 1fr);
    gap: 1rem;
    text-transform: uppercase;
  }

  #processing-message {
    font-size: 1rem;
  }

  .back {
    width: fit-content;
    padding: 0.5rem;
    height: 3rem;
    font-size: 1.3rem;
    border-radius: 0.5rem;
    display: inline-flex;
    align-items: center;
    background-color: #fff;
    box-shadow: 1px 1px 0.5rem rgba(0, 0, 0, 0.15);
    color: #777;
    cursor: pointer;
  }

  .back-icon {
    margin-right: 0.5rem;
  }

  .previous-logo-heading {
    font-weight: bold;
    color: #666;
    letter-spacing: 1px;
    font-size: 1.2rem;
    text-transform: uppercase;
    margin-bottom: 0.5rem;
    display: block;
  }

  .previous-logos {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    grid-gap: 0.5rem;
    margin-bottom: 2rem;
  }

  .previous-logo img {
    max-width: 100%;
  }

  .previous-logo {
    cursor: pointer;
    border: 1px solid rgba(0, 0, 0, 0.15);
    padding: 1rem;
    border-radius: 1rem;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.15);
  }

  .preview {
    text-align: center;
    font-weight: bold;
    font-size: 1.3rem;
    color: #666;
    text-transform: uppercase;
    letter-spacing: 1px;
  }

  .preview img {
    background-color: var(--bgColour);
    padding: 1rem;
    max-height: 90%;
  }

  .heading {
    font-weight: bold;
    color: #666;
    letter-spacing: 1px;
    font-size: 1.2rem;
    text-transform: uppercase;
    margin-top: 2rem;
    display: block;
    padding-bottom: 0.5rem;
    border-bottom: solid 1px rgba(0, 0, 0, 0.15);
    margin-bottom: -0.5rem;
  }

  .more-info {
    float: right;
    text-transform: lowercase;
    width: 1.6rem;
    height: 1.6rem;
    font-size: 1rem;
    font-weight: 700;
    border-radius: 4rem;
    background-color: #fe5900;
    color: #fff;
    display: flex;
    font-family: 'Oswald', sans-serif;
    justify-content: center;
    align-items: center;
    cursor: pointer;
  }

  .processed-label {
    margin-top: 2rem;
    font-weight: bold;
    color: #666;
    letter-spacing: 1px;
    font-size: 1.2rem;
    text-transform: uppercase;
    margin-bottom: 0.5rem;
    display: block;
  }

  .processed-images {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    grid-gap: 1rem;
  }

  .colourpicker {
    margin-top: 1rem;
    width: 50%;
  }

  .print-type-selection {
    margin-top: 2rem;
  }
</style>
